import {
  forgetLayout,
  getSeatMap,
  InvalidInput,
  NumberField,
  setLayout,
  type SeatLayout,
  type SeatMapData,
} from '@trippath/shared';
import { useEffect, useId, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import {
  placeOf,
  planOf,
  rowWhere,
  widest,
  windowBeside,
  type SeatPlan,
} from '../../lib/seats';

/** A seat marked on the map: the traveller's own, or a fellow traveller's. */
export interface MarkedSeat {
  seat: string;
  own: boolean;
}

/** Pixels a metre in the whole aircraft, and in the rows around the seat. */
const WHOLE = 12;
const CLOSE = 64;

/** The aircraft from above, nose up, to scale: wings, tail, fuselage, windows, doors, exits, seats. */
function Whole({ plan, marked }: { plan: SeatPlan; marked: MarkedSeat[] }) {
  const { t } = useTranslation();
  const w = plan.span * WHOLE + 20;
  const h = plan.length * WHOLE + 20;
  const cx = w / 2;
  const y = (m: number) => 10 + m * WHOLE;
  const half = (plan.width / 2) * WHOLE;
  const tip = (plan.span / 2) * WHOLE;
  const L = plan.length;
  const [rootFront, rootBack, tipFront, tipBack] = plan.wing;
  // As wide as the fuselage lets the most seats across, its aisles counted.
  const seatW = (plan.width * 0.86 * WHOLE) / widest(plan);
  const marks = new Map(marked.map((m) => [m.seat.replace(/\s/g, '').toUpperCase(), m.own]));
  const shapes: ReactNode[] = [];
  // Where one's own seat is, to point at it.
  const mine = ownSpot(plan, marked, cx, seatW, y);
  for (const side of [-1, 1]) {
    shapes.push(
      <path
        key={`wing${String(side)}`}
        className="seatmap-wing"
        d={`M${String(cx + side * half)},${String(y(rootFront * L))} L${String(cx + side * tip)},${String(y(tipFront * L))} L${String(cx + side * tip)},${String(y(tipBack * L))} L${String(cx + side * half)},${String(y(rootBack * L))} Z`}
      />,
      <path
        key={`tail${String(side)}`}
        className="seatmap-wing"
        d={`M${String(cx + side * half * 0.3)},${String(y(L * 0.86))} L${String(cx + side * tip * 0.34)},${String(y(L * 0.95))} L${String(cx + side * tip * 0.34)},${String(y(L * 0.98))} L${String(cx)},${String(y(L * 0.96))} Z`}
      />,
    );
  }
  const body = `M${String(cx - half)},${String(y(L * 0.12))} C${String(cx - half)},${String(y(L * 0.04))} ${String(cx - half * 0.45)},${String(y(0))} ${String(cx)},${String(y(0))} C${String(cx + half * 0.45)},${String(y(0))} ${String(cx + half)},${String(y(L * 0.04))} ${String(cx + half)},${String(y(L * 0.12))} L${String(cx + half)},${String(y(L * 0.76))} C${String(cx + half)},${String(y(L * 0.9))} ${String(cx + half * 0.25)},${String(y(L))} ${String(cx)},${String(y(L))} C${String(cx - half * 0.25)},${String(y(L))} ${String(cx - half)},${String(y(L * 0.9))} ${String(cx - half)},${String(y(L * 0.76))} Z`;
  return (
    <svg
      className="seatmap-whole"
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      role="img"
      aria-label={t('seatMap.whole')}
    >
      {shapes}
      <path className="seatmap-body" d={body} />
      {plan.windows.map((m) =>
        [-1, 1].map((side) => (
          <rect
            key={`${String(m)}${String(side)}`}
            className="seatmap-window"
            x={cx + side * (half - 1.5) - 0.8}
            y={y(m) - 1.8}
            width={1.6}
            height={3.6}
            rx={0.8}
          />
        )),
      )}
      {[
        ...plan.doors.map((m) => ({ kind: 'door', m })),
        ...plan.exits.map((m) => ({ kind: 'exit', m })),
      ].map(({ kind, m }) =>
        [-1, 1].map((side) => (
          <rect
            key={`${kind}${String(m)}${String(side)}`}
            className={`seatmap-${kind}`}
            x={cx + side * half - 1.5}
            y={y(m) - (kind === 'door' ? 6 : 4.5)}
            width={3}
            height={kind === 'door' ? 12 : 9}
            rx={1}
          />
        )),
      )}
      {plan.rows.map((row) => {
        const across = row.groups.reduce((n, g) => n + g.length, 0) + row.groups.length - 1;
        let x = cx - (across * seatW) / 2;
        return row.groups.map((group, gi) => {
          if (gi > 0) x += seatW;
          return group.map((letter) => {
            const own = marks.get(`${String(row.number)}${letter}`);
            const left = x;
            x += seatW;
            return (
              <rect
                key={`${String(row.number)}${letter}`}
                className={
                  own === undefined
                    ? `seatmap-seat seatmap-${row.cabinClass === 'Y' ? 'economy' : row.cabinClass === 'W' ? 'premium' : 'business'}`
                    : own
                      ? 'seatmap-seat seatmap-own'
                      : 'seatmap-seat seatmap-mate'
                }
                x={left + 0.4}
                y={y(row.at) + 0.4}
                width={seatW - 0.8}
                height={Math.min(row.pitch, 0.55) * WHOLE - 0.8}
                rx={1}
              />
            );
          });
        });
      })}
      {callout(mine, cx + tip * 0.55)}
    </svg>
  );
}

/** One's own seat's right edge and top in a drawing, and the seat; none when not on the plan. */
function ownSpot(
  plan: SeatPlan,
  marked: MarkedSeat[],
  cx: number,
  seatW: number,
  y: (m: number) => number,
): { x: number; y: number; seat: string } | null {
  const own = marked.find((m) => m.own);
  const place = own ? placeOf(plan, own.seat) : null;
  const row = place && plan.rows.find((r) => r.number === place.row);
  if (!place || !row) return null;
  const letters = row.groups.flatMap((group, at) => (at === 0 ? group : ['', ...group]));
  const left = cx - (letters.length * seatW) / 2 + letters.indexOf(place.letter) * seatW;
  return { x: left + seatW, y: y(row.at), seat: `${String(row.number)}${place.letter}` };
}

/** A line from one's own seat out over the wing, and the seat said there. */
function callout(mine: { x: number; y: number; seat: string } | null, to: number): ReactNode {
  return (
    mine && (
      <g className="seatmap-callout">
        <line x1={mine.x + 1} y1={mine.y + 3} x2={to} y2={mine.y + 3} />
        <text x={to + 8} y={mine.y + 14}>
          {mine.seat}
        </text>
      </g>
    )
  );
}

/** The rows around a seat, close: its seats, the windows beside them, the exits and doors. */
function Close({ plan, marked, row }: { plan: SeatPlan; marked: MarkedSeat[]; row: number }) {
  const { t } = useTranslation();
  const at = plan.rows.findIndex((r) => r.number === row);
  const near = plan.rows.slice(Math.max(0, at - 2), at + 3);
  const from = Math.min(...near.map((r) => r.at)) - 0.5;
  const to = Math.max(...near.map((r) => r.at + r.pitch)) + 0.3;
  const half = (plan.width / 2) * CLOSE;
  const w = plan.width * CLOSE + 80;
  const h = (to - from) * CLOSE;
  const cx = w / 2;
  const y = (m: number) => (m - from) * CLOSE;
  const seatW = (plan.width * 0.86 * CLOSE) / widest(plan);
  const marks = new Map(marked.map((m) => [m.seat.replace(/\s/g, '').toUpperCase(), m.own]));
  return (
    <svg
      className="seatmap-close"
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      role="img"
      aria-label={t('seatMap.close', { row })}
    >
      <rect className="seatmap-body" x={cx - half} y={0} width={half * 2} height={h} />
      {plan.windows
        .filter((m) => m > from - 0.3 && m < to + 0.3)
        .map((m) =>
          [-1, 1].map((side) => (
            <rect
              key={`${String(m)}${String(side)}`}
              className="seatmap-window"
              x={cx + side * (half - 4) - 3.5}
              y={y(m) - 9}
              width={7}
              height={18}
              rx={3.5}
            />
          )),
        )}
      {near.map((r) => {
        const across = r.groups.reduce((n, g) => n + g.length, 0) + r.groups.length - 1;
        let x = cx - (across * seatW) / 2;
        return (
          <g key={r.number}>
            <text
              className="seatmap-row"
              x={cx - half - 8}
              y={y(r.at) + 0.3 * CLOSE}
              textAnchor="end"
            >
              {r.number}
            </text>
            {r.exit && (
              <text className="seatmap-exit-word" x={cx + half + 8} y={y(r.at) + 0.3 * CLOSE}>
                {t('seatMap.exitRow')}
              </text>
            )}
            {r.groups.map((group, gi) => {
              if (gi > 0) x += seatW;
              return group.map((letter) => {
                const own = marks.get(`${String(r.number)}${letter}`);
                const left = x;
                x += seatW;
                return (
                  <g key={letter}>
                    <rect
                      className={
                        own === undefined
                          ? 'seatmap-seat seatmap-economy'
                          : own
                            ? 'seatmap-seat seatmap-own'
                            : 'seatmap-seat seatmap-mate'
                      }
                      x={left + 2}
                      y={y(r.at) + 2}
                      width={seatW - 4}
                      height={Math.min(r.pitch, 0.55) * CLOSE - 4}
                      rx={5}
                    />
                    <text
                      className={
                        own === undefined
                          ? 'seatmap-letter'
                          : 'seatmap-letter seatmap-letter-on'
                      }
                      x={left + seatW / 2}
                      y={y(r.at) + 0.3 * CLOSE}
                      textAnchor="middle"
                    >
                      {letter}
                    </text>
                  </g>
                );
              });
            })}
          </g>
        );
      })}
    </svg>
  );
}

/** An airline's layout of the type, to correct: kept for every flight of it. */
function LayoutForm({
  icao,
  name,
  layout,
  airline,
  onSaved,
}: {
  icao: string;
  name: string;
  layout: SeatLayout;
  airline: string;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const [classes, setClasses] = useState(layout.classes);
  const [pitch, setPitch] = useState<number | null>(layout.pitchCm);
  const [skip, setSkip] = useState(layout.skipRows);
  const [exits, setExits] = useState(layout.exitRows);
  const [wrong, setWrong] = useState<string[]>([]);
  const [failed, setFailed] = useState(false);
  const text = (
    key: string,
    label: string,
    value: string,
    set: (v: string) => void,
    hint: string,
  ) => (
    <div className="field">
      <label className="field-label" htmlFor={`${id}-${key}`}>
        {label}
      </label>
      <input
        id={`${id}-${key}`}
        className="text-input mono"
        type="text"
        autoComplete="off"
        placeholder={hint}
        value={value}
        aria-invalid={wrong.includes(key)}
        onChange={(event) => {
          set(event.target.value);
        }}
      />
    </div>
  );
  return (
    <form
      className="seatmap-layout"
      onSubmit={(event) => {
        event.preventDefault();
        setFailed(false);
        setLayout(airline, icao, {
          classes,
          pitchCm: pitch,
          skipRows: skip,
          exitRows: exits,
        }).then(onSaved, (error: unknown) => {
          if (error instanceof InvalidInput) setWrong(error.fields.map((f) => f.field));
          else setFailed(true);
        });
      }}
    >
      <p className="quiet">{t('seatMap.layoutLead', { airline, model: name })}</p>
      <div className="form-row">
        {text('classes', t('seatMap.classes'), classes, setClasses, 'C12 Y168')}
        <NumberField
          label={t('seatMap.pitch')}
          value={pitch}
          onChange={setPitch}
          min={60}
          max={220}
        />
        {text('skipRows', t('seatMap.skipRows'), skip, setSkip, '13')}
        {text('exitRows', t('seatMap.exitRows'), exits, setExits, '11, 12')}
      </div>
      {wrong.length > 0 && (
        <p className="field-error" role="alert">
          {t('seatMap.layoutWrong')}
        </p>
      )}
      {failed && (
        <p className="form-failed" role="alert">
          {t('seatMap.failed')}
        </p>
      )}
      <div className="actions">
        <button type="submit" className="button button-primary button-small">
          {t('seatMap.save')}
        </button>
        {layout.source === 'own' && (
          <button
            type="button"
            className="button button-small"
            onClick={() => {
              forgetLayout(airline, icao).then(onSaved, () => {
                setFailed(true);
              });
            }}
          >
            {t('seatMap.restore')}
          </button>
        )}
      </div>
    </form>
  );
}

/**
 * A seat map of the aircraft a flight goes with, as its airline lays it out: the whole aircraft to
 * scale and the rows around the seat close, the seat's place — by a window, in the middle or on an
 * aisle, which side, whether a window is beside it — what the drawing is from, and the airline's
 * layout to correct.
 */
export function SeatMap({
  icao,
  airline,
  marked,
}: {
  icao: string;
  airline: string | null;
  marked: MarkedSeat[];
}) {
  const { t } = useTranslation();
  const [map, setMap] = useState<SeatMapData | null>(null);
  const [failed, setFailed] = useState(false);
  const [asked, setAsked] = useState(0);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    getSeatMap(icao, airline).then(setMap, () => {
      setFailed(true);
    });
  }, [icao, airline, asked]);

  if (failed) {
    return (
      <p className="form-failed" role="alert">
        {t('seatMap.failed')}
      </p>
    );
  }
  if (!map) return <p className="quiet">{t('trips.loading')}</p>;
  const plan = planOf(map);
  if (!plan)
    return <p className="quiet">{t('seatMap.unknown', { model: map.model ?? icao })}</p>;
  const own = marked.find((m) => m.own) ?? marked[0];
  const place = own && placeOf(plan, own.seat);
  const where = place ? rowWhere(plan, place.row) : null;
  const name = map.model ?? icao;
  return (
    <div className="seatmap">
      <div className="seatmap-pictures">
        <Whole plan={plan} marked={marked} />
      </div>
      <div className="seatmap-side">
        {own && place ? (
          <>
            <h3 className="seatmap-title">
              {place.side === 'centre'
                ? t('seatMap.placeCentre', { seat: own.seat.toUpperCase() })
                : t('seatMap.place', {
                    seat: own.seat.toUpperCase(),
                    kind: t(`seatMap.kind.${place.kind}`),
                    side: t(`seatMap.side.${place.side}`),
                  })}
            </h3>
            {place.kind === 'window' &&
              (windowBeside(plan, place.row) ? (
                <p className="seatmap-window-said">{t('seatMap.windowBeside')}</p>
              ) : (
                <p className="quiet seatmap-window-not">{t('seatMap.noWindow')}</p>
              ))}
            <Close plan={plan} marked={marked} row={place.row} />
          </>
        ) : (
          own && <p className="quiet">{t('seatMap.notOnMap', { seat: own.seat })}</p>
        )}
        <dl className="seatmap-facts">
          {/* Where its row is: by the wing, and by a door to be out among the first. */}
          {where && place && (
            <>
              <dt>{t('seatMap.row', { row: place.row })}</dt>
              <dd>
                {[
                  t(`seatMap.wing.${where.wing}`),
                  ...(where.door ? [t('seatMap.byDoor')] : []),
                ].join(' · ')}
              </dd>
            </>
          )}
          <dt>{t('seatMap.type')}</dt>
          <dd>
            {name} <span className="mono">{icao}</span>
          </dd>
          {map.dimensions?.lengthM != null && (
            <>
              <dt>{t('seatMap.size')}</dt>
              <dd className="mono">
                {t('seatMap.sizeSaid', {
                  length: map.dimensions.lengthM.toFixed(1),
                  span: (map.dimensions.wingspanM ?? 0).toFixed(1),
                })}
                <span className="seatmap-source">FAA</span>
              </dd>
            </>
          )}
          {/* The fuselage as its maker's planning documents say it. */}
          <dt>{t('seatMap.body')}</dt>
          <dd className="mono">
            {t('seatMap.bodySaid', {
              width: plan.width.toFixed(2),
              frame: Math.round(plan.frame * 100),
            })}
            <span className="seatmap-source">{t('seatMap.maker')}</span>
          </dd>
          <dt>{t('seatMap.doors')}</dt>
          <dd>
            {t('seatMap.doorsSaid', { doors: plan.doors.length, exits: plan.exits.length })}
            <span className="seatmap-source">{t('seatMap.maker')}</span>
          </dd>
          <dt>{t('seatMap.layout')}</dt>
          <dd className="mono">
            {plan.layout.classes} · {t('seatMap.rows', { count: plan.rows.length })}
            <span className="seatmap-source">{t(`seatMap.source.${plan.layout.source}`)}</span>
          </dd>
        </dl>
        <div className="seatmap-legend">
          <span>
            <i className="seatmap-own" /> {t('seatMap.own')}
          </span>
          <span>
            <i className="seatmap-mate" /> {t('seatMap.mate')}
          </span>
          <span>
            <i className="seatmap-window" /> {t('seatMap.window')}
          </span>
          <span>
            <i className="seatmap-exit" /> {t('seatMap.exit')}
          </span>
        </div>
        <p className="quiet seatmap-note">{t('seatMap.note')}</p>
        {airline !== null &&
          (editing ? (
            <LayoutForm
              icao={icao}
              name={name}
              layout={plan.layout}
              airline={airline}
              onSaved={() => {
                setEditing(false);
                setAsked((n) => n + 1);
              }}
            />
          ) : (
            <button
              type="button"
              className="button button-small"
              onClick={() => {
                setEditing(true);
              }}
            >
              {t('seatMap.correct', { airline })}
            </button>
          ))}
      </div>
    </div>
  );
}
